// One practice case, with the شرح حال the student wrote into it.
//
// ── `null` is a STATE, not an error ────────────────────────────────────────
//
// `myCase` returns null both when the case does not exist and when it belongs to
// another student — DELIBERATELY indistinguishable, so an error response cannot
// be used to confirm which case ids exist on the platform one id at a time
// (SCHEMA.graphql header; CORE.md §3). There is no `CASE_FORBIDDEN` code and
// there must not be one.
//
// So a null answer is NOT an error: it must not set `error`, must not offer a
// retry, and must render exactly ONE screen — «این مورد در دسترس نیست» — never
// «متعلق به شما نیست», which would be the same oracle spoken in Persian.
//
// ── Why `entries` is re-read on its own after a save ──────────────────────
//
// `caseHistory` is redundant with `myCase.entries` and the frozen schema keeps it
// anyway, for exactly this: the editor saves a section and needs the entries
// back, not the case and its counts. Re-reading the whole case after every
// keystroke-sized save would repaint the header — the title, the counts, the
// «آخرین تغییر» — under a student who is mid-sentence in a textarea below it.

import React from 'react';

import { bffQuery, bffMutation, errorCode } from '../lib/bffClient.js';
import {
  MY_CASE_QUERY,
  CASE_HISTORY_QUERY,
  UPDATE_CASE_MUTATION,
  ADD_CASE_HISTORY_MUTATION,
  UPDATE_HISTORY_ENTRY_MUTATION,
  DELETE_HISTORY_ENTRY_MUTATION,
} from '../graphql/documents.js';

export function useCase(caseId) {
  const [caseRecord, setCaseRecord] = React.useState(null);
  const [entries, setEntries] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [nonce, setNonce] = React.useState(0);

  React.useEffect(() => {
    if (!caseId) {
      setLoading(false);
      return undefined;
    }
    let alive = true;
    setLoading(true);

    bffQuery(MY_CASE_QUERY, { caseId })
      .then((data) => {
        if (!alive) return;
        const row = data?.myCase || null;
        setCaseRecord(row);
        setEntries(row?.entries || []);
        // Not an error. See the header: null is "not available", full stop.
        setError(null);
      })
      .catch((err) => {
        if (!alive) return;
        console.error('[useCase] failed:', errorCode(err) || err?.message);
        setError(err);
        setCaseRecord(null);
        setEntries([]);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });

    return () => {
      alive = false;
    };
  }, [caseId, nonce]);

  const reload = React.useCallback(() => setNonce((n) => n + 1), []);

  /** Pull the history back without disturbing the case header. */
  const reloadHistory = React.useCallback(async () => {
    if (!caseId) return;
    try {
      const data = await bffQuery(CASE_HISTORY_QUERY, { caseId, sections: null });
      setEntries(data?.caseHistory || []);
    } catch (err) {
      console.error('[useCase] history reload failed:', errorCode(err) || err?.message);
      // Deliberately NOT cleared: the entries on screen are the ones that were
      // successfully read a moment ago, and blanking them because a refetch
      // failed would look to the student like their writing was lost.
    }
  }, [caseId]);

  /**
   * Update the case. OMITTED FIELDS ARE LEFT ALONE; `''` (or 0 for ageYears)
   * clears one.
   *
   * The caller builds the input from what actually CHANGED — see
   * ../screens/CaseDetail/CaseDetail.shared.js. Sending the whole form every
   * time would work only as long as the form holds every field; the day it holds
   * a subset, a student editing the title erases the rest.
   */
  const updateCase = React.useCallback(
    async (input) => {
      const data = await bffMutation(UPDATE_CASE_MUTATION, { caseId, input });
      const row = data?.updateCase;
      // `updateCase` answers with the CaseRow shape — no `entries` — so the
      // entries already in state are kept rather than being overwritten with
      // undefined.
      if (row) setCaseRecord((prev) => ({ ...(prev || {}), ...row }));
      return row || null;
    },
    [caseId],
  );

  /**
   * Append entries. ALL-OR-NOTHING, and plural for a reason: the voice-intake
   * flow submits a whole drafted history at once, and half a history is worse
   * than none — a case whose سابقه landed and whose داروها did not reads to the
   * model as a patient on no drugs.
   */
  const addHistory = React.useCallback(
    async (rows) => {
      const data = await bffMutation(ADD_CASE_HISTORY_MUTATION, { caseId, entries: rows });
      const added = data?.addCaseHistory || [];
      if (added.length) setEntries((prev) => [...prev, ...added]);
      return added;
    },
    [caseId],
  );

  /**
   * Edit one entry.
   *
   * `section` and `source` are not in the update input and cannot be sent:
   * provenance is a fact about how a row came to exist (CORE.md §6), and an
   * editable one would let a voice draft become indistinguishable from a
   * sentence the student wrote themselves.
   */
  const updateEntry = React.useCallback(async (entryId, input) => {
    const data = await bffMutation(UPDATE_HISTORY_ENTRY_MUTATION, { entryId, input });
    const row = data?.updateCaseHistoryEntry;
    if (row) setEntries((prev) => prev.map((e) => (e.id === row.id ? row : e)));
    return row || null;
  }, []);

  const deleteEntry = React.useCallback(async (entryId) => {
    await bffMutation(DELETE_HISTORY_ENTRY_MUTATION, { entryId });
    setEntries((prev) => prev.filter((e) => e.id !== entryId));
  }, []);

  return {
    caseRecord,
    entries,
    loading,
    error,
    // `notFound` is separate from `error` because they are different screens and
    // only one of them gets a retry button.
    notFound: !loading && !error && !caseRecord,
    reload,
    reloadHistory,
    updateCase,
    addHistory,
    updateEntry,
    deleteEntry,
  };
}

export default useCase;
